import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, EmptyState, IconButton, Page, PageHeader, Pagination, SearchInput, Segmented, TableSkeleton } from "../../../components/ui";
import { getActiveUsers } from "../../../services/activeUsers";
import { getUser } from "../../../services/users";
import { getUserInfo } from "../../../store/actions/uploadMediaAction";
import Chart from "react-apexcharts";
import { formatDuration } from "../video-performance/VideoPerformance";
import "../payments-ledger/payments.css";
import "../video-performance/video-performance.css";
import "./active-users.css";

const PERIODS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "1 year" },
];

const SORTS = [
  { value: "days", label: "Active days" },
  { value: "score", label: "Engagement score" },
  { value: "change", label: "Biggest rise" },
  { value: "videos", label: "Videos watched" },
  { value: "watchTime", label: "Watch time" },
  { value: "comments", label: "Comments" },
  { value: "likes", label: "Likes" },
  { value: "thanks", label: "Merci sent" },
  { value: "donations", label: "Donations" },
];

const FILTERS = [
  { value: "all", label: "Everyone" },
  { value: "premium", label: "Premium" },
  { value: "new", label: "Newly active" },
  { value: "falling", label: "Slowing down" },
];

const muted = { colors: "#9b9ba6" };
const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const dayLabel = (key) => new Date(`${key}T00:00:00Z`).toLocaleDateString([], { day: "numeric", month: "short", timeZone: "UTC" });

/** Active users per day (columns). */
export const dailyChart = (daily, t) => {
  const step = Math.max(1, Math.ceil(daily.length / 8));
  const shown = new Set(daily.filter((d, i) => (daily.length - 1 - i) % step === 0).map((d) => d.day));
  return {
    series: [{ name: t("Active users"), data: daily.map((d) => d.active) }],
    options: {
      chart: { id: "active-daily", background: "transparent", toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, fontFamily: "inherit" },
      colors: ["#1176BC"],
      dataLabels: { enabled: false },
      plotOptions: { bar: { columnWidth: daily.length > 40 ? "80%" : "55%", borderRadius: 2 } },
      grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
      xaxis: { categories: daily.map((d) => d.day), labels: { style: muted, rotate: 0, hideOverlappingLabels: false, formatter: (v) => (v && shown.has(v) ? dayLabel(v) : "") }, axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false } },
      yaxis: { min: 0, forceNiceScale: true, labels: { style: muted, formatter: (v) => fmt(Math.round(v)) } },
      tooltip: { theme: "dark", x: { formatter: (v, { dataPointIndex }) => (daily[dataPointIndex] ? dayLabel(daily[dataPointIndex].day) : "") } },
    },
  };
};

/** How many active days the users of the period had. */
export const loyaltyChart = (loyalty, t) => ({
  series: [{ name: t("Users"), data: loyalty.map((b) => b.users) }],
  options: {
    chart: { id: "active-loyalty", background: "transparent", toolbar: { show: false }, animations: { enabled: false }, fontFamily: "inherit" },
    colors: ["#CDAE6E"],
    dataLabels: { enabled: true, formatter: (v) => fmt(v), style: { colors: ["#111"] } },
    plotOptions: { bar: { horizontal: true, barHeight: "60%", borderRadius: 3 } },
    grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
    xaxis: { categories: loyalty.map((b) => t(b.label)), labels: { style: muted, formatter: (v) => fmt(Math.round(v)) } },
    yaxis: { labels: { style: muted } },
    tooltip: { theme: "dark", y: { formatter: (v) => fmt(v) } },
  },
});

/** Change of the active days against the previous period of the same length. */
const Trend = ({ item }) => {
  const { t } = useTranslation();
  const before = t("{{count}} active days over the previous period", { count: item.daysBefore });
  if (item.trend === "joined") return <Badge color="blue">{t("New member")}</Badge>;
  if (item.trend === "back") return <Badge color="green">{t("Back again")}</Badge>;
  if (item.trend === "falling") return <span title={before}><Badge color="red">{t("Slowing down")} · {t("{{count}} d before", { count: item.daysBefore })}</Badge></span>;
  if (item.trend === "up" || item.trend === "down") {
    const gap = item.days - item.daysBefore;
    return (
      <small className="perf-delta" data-tone={item.trend} title={before}>
        <i className={item.trend === "up" ? "ri-arrow-up-line" : "ri-arrow-down-line"} aria-hidden="true"></i>
        {gap > 0 ? "+" : ""}
        {gap} {t("d")}
      </small>
    );
  }
  return null;
};
const ago = (date, t) => {
  if (!date) return "—";
  const days = Math.floor((Date.now() - new Date(date).getTime()) / 864e5);
  if (days <= 0) return t("Today");
  if (days === 1) return t("Yesterday");
  return t("{{count}} days ago", { count: days });
};

/** CSV of the current page (opens in Excel / Google Sheets). */
const exportCsv = (data, t) => {
  const head = ["Rang", "Nom", "E-mail", "Téléphone", "Pays", "Premium", "Jours actifs", "Jours actifs (période précédente)", "Régularité %", "Vidéos", "Temps regardé (min)", "Commentaires", "Réponses", "Merci", "J'aime vidéos", "J'aime commentaires", "À regarder plus tard", "Dons", "Score", "Dernière activité"];
  const rows = data.items.map((i) => [
    i.rank, i.user.name, i.user.email, i.user.phone, i.user.country, i.user.premium ? "oui" : "non", i.days, i.daysBefore ?? "", i.regularity, i.videos, Math.round((i.watchSeconds || 0) / 60), i.comments, i.replies,
    i.thanks, i.videoLikes, i.commentLikes, i.watchlist, i.donations, i.score, i.user.lastActiveAt ? new Date(i.user.lastActiveAt).toISOString().slice(0, 10) : "",
  ]);
  const csv = [head, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `utilisateurs-actifs-${data.filter && data.filter !== "all" ? `${data.filter}-` : ""}${data.period}j-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Most active users over a period: active days (app or website) and what they
 * do — videos watched, comments and replies, Merci, likes, watchlist,
 * donations — ranked by active days, engagement score or one action.
 */
const ActiveUsers = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [days, setDays] = useState(30);
  const [sort, setSort] = useState("days");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  const load = useCallback(
    (fresh) => {
      setData(null);
      getActiveUsers({ days, sort, page, q: search.trim(), ...(filter !== "all" ? { filter } : {}), ...(fresh === true ? { fresh: 1 } : {}) }).then(setData);
    },
    [days, sort, page, search, filter]
  );
  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const reset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };
  const openUser = (id) =>
    getUser(id).then((user) => {
      dispatch(getUserInfo(user));
      history.push("/dashboard/user-info");
    });

  const totals = data?.totals;
  const change = (now, before) => {
    if (!before) return null;
    const value = Math.round(((now - before) / before) * 100);
    return { text: `${value > 0 ? "+" : ""}${value} %`, tone: value > 0 ? "up" : value < 0 ? "down" : "flat" };
  };
  const card = (value, label, options = {}) => {
    const body = (
      <>
        <strong>{totals ? value : "—"}</strong>
        <span>{label}</span>
        {options.change && (
          <small className="perf-delta" data-tone={options.change.tone}>
            {options.change.text} {t("vs previous period")}
          </small>
        )}
        {options.hint && <small className="ui-hint">{options.hint}</small>}
      </>
    );
    return options.onClick ? (
      <button type="button" className="ui-card ledger-stat active-users-stat-link" title={options.title} onClick={options.onClick} aria-pressed={options.pressed}>
        {body}
      </button>
    ) : (
      <div className="ui-card ledger-stat" title={options.title}>
        {body}
      </div>
    );
  };
  const daily = data?.daily?.length ? dailyChart(data.daily, t) : null;
  const loyalty = data?.loyalty?.some((b) => b.users) ? loyaltyChart(data.loyalty, t) : null;

  return (
    <Page>
      <PageHeader
        title={t("Most active users")}
        subtitle={t("Who uses the platform the most: days of activity on the app or the website, and what they do there.")}
        actions={
          <>
            {data?.items?.length > 0 && <IconButton icon="ri-download-2-line" label={t("Export (CSV)")} onClick={() => exportCsv(data, t)} />}
            <IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={() => load(true)} />
          </>
        }
      />

      <div className="perf-filters">
        <Segmented label={t("Period")} value={days} onChange={reset(setDays)} options={PERIODS.map((p) => ({ ...p, label: t(p.label) }))} />
        <Segmented label={t("Users")} value={filter} onChange={reset(setFilter)} options={FILTERS.map((f) => ({ ...f, label: t(f.label) }))} />
        {data?.computedAt && (
          <span className="ui-hint perf-computed">{t("Figures of {{time}}", { time: new Date(data.computedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}</span>
        )}
      </div>

      <div className="ledger-stats perf-stats">
        {card(fmt(totals?.activeUsers), t("Active users"), { change: change(totals?.activeUsers, totals?.previousActiveUsers) })}
        {card(fmt(totals?.avgDaily), t("Active per day (average)"))}
        {card(`${totals?.stickiness || 0} %`, t("Daily loyalty"), { title: t("Average share of the active users of the period who come on a given day") })}
        {card(fmt(totals?.newActive), t("Newly active"), { hint: t("not active over the previous period"), onClick: () => reset(setFilter)("new"), pressed: filter === "new" })}
        {card(fmt(totals?.falling), t("Slowing down"), { hint: t("regulars who come half as often or less"), onClick: () => reset(setFilter)("falling"), pressed: filter === "falling" })}
        {card(formatDuration(totals?.watchSeconds), t("Watch time"))}
        {card(fmt(totals?.comments), t("Comments and replies"))}
        {card(fmt(totals?.thanks), t("Merci sent"))}
      </div>

      {(daily || loyalty) && (
        <div className="perf-charts">
          {daily && (
            <section className="ui-card perf-top">
              <h2>{t("Active users per day")}</h2>
              <Chart type="bar" height={260} options={daily.options} series={daily.series} />
            </section>
          )}
          {loyalty && (
            <section className="ui-card perf-top">
              <h2>{t("Loyalty: active days over the period")}</h2>
              <Chart type="bar" height={260} options={loyalty.options} series={loyalty.series} />
            </section>
          )}
        </div>
      )}

      <section className="ui-card">
        <div className="ui-toolbar active-users-toolbar">
          <SearchInput value={search} onChange={reset(setSearch)} placeholder={t("Name, email or phone")} />
          {filter !== "falling" && (
            <select className="ui-input active-users-sort" aria-label={t("Rank by")} value={sort} onChange={(e) => reset(setSort)(e.target.value)}>
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {t("Rank by")} : {t(s.label)}
                </option>
              ))}
            </select>
          )}
        </div>
        {filter === "falling" && (
          <p className="ui-hint active-users-note">
            <i className="ri-information-line" aria-hidden="true"></i>{" "}
            {t("Users who were active at least 3 days over the previous period and come half as often or less now, biggest drops first. A good list to reach out to before they leave.")}
          </p>
        )}

        {!data ? (
          <TableSkeleton />
        ) : !data.items.length ? (
          <EmptyState icon="ri-user-search-line">{t("No activity over this period")}</EmptyState>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table active-users-table perf-table">
              <thead>
                <tr>
                  <th className="perf-sticky perf-sticky-rank">#</th>
                  <th className="perf-sticky perf-sticky-title">{t("User")}</th>
                  <th>{t("Active days")}</th>
                  <th>{t("Videos")}</th>
                  <th>{t("Watch time")}</th>
                  <th>{t("Comments")}</th>
                  <th>{t("Merci")}</th>
                  <th>{t("Likes")}</th>
                  <th>{t("Watch later")}</th>
                  <th>{t("Donations")}</th>
                  <th title={t("Weighted sum of the actions (active days count the most)")}>{t("Score")}</th>
                  <th>{t("Last activity")}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.user.id} className="ledger-row" onClick={() => openUser(item.user.id)}>
                    <td className="active-users-rank perf-sticky perf-sticky-rank" data-top={item.rank <= 3 || undefined}>
                      {item.rank}
                    </td>
                    <td className="perf-sticky perf-sticky-title">
                      <div className="active-users-who">
                        {item.user.image ? (
                          <img src={item.user.image} alt="" className="active-users-avatar" />
                        ) : (
                          <span className="active-users-avatar" aria-hidden="true">
                            {item.user.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="active-users-id">
                          <span className="ui-media-title">
                            {item.user.name} {item.user.premium && <Badge color="amber">Premium</Badge>}
                          </span>
                          <span className="ui-media-meta">{item.user.email || item.user.phone || "—"}</span>
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="active-users-days">
                        <strong>{item.days}</strong> / {data.period}
                      </span>
                      <span className="active-users-bar" aria-hidden="true">
                        <span style={{ width: `${Math.min(100, item.regularity)}%` }} />
                      </span>
                      <span className="active-users-trend">
                        <Trend item={item} />
                      </span>
                    </td>
                    <td>{fmt(item.videos)}</td>
                    <td>{item.watchSeconds ? formatDuration(item.watchSeconds) : "—"}</td>
                    <td>
                      {fmt(item.comments)}
                      {item.replies > 0 && <span className="ui-media-meta">+ {fmt(item.replies)} {t("replies")}</span>}
                    </td>
                    <td>{fmt(item.thanks)}</td>
                    <td>
                      {fmt(item.videoLikes + item.commentLikes)}
                      {item.commentLikes > 0 && (
                        <span className="ui-media-meta">
                          {fmt(item.commentLikes)} {t("on comments")}
                        </span>
                      )}
                    </td>
                    <td>{fmt(item.watchlist)}</td>
                    <td>{fmt(item.donations)}</td>
                    <td>
                      <strong>{fmt(item.score)}</strong>
                    </td>
                    <td className="ui-cell-muted">{ago(item.user.lastActiveAt, t)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && <Pagination page={data.page} pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))} total={data.total} pageSize={data.pageSize} onChange={setPage} />}
        <p className="ui-hint active-users-hint">
          {t("Active day: at least one use of the app or the website that day. Score: active day ×3, comment ×4, reply ×3, Merci ×8, donation ×8, video watched, like or watch-later ×1.")}{" "}
          {t("Under the active days: change against the previous period of the same length. Watch time is estimated from where each play stopped.")}
        </p>
      </section>
    </Page>
  );
};

export default ActiveUsers;
